import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormResponseValue } from "@tellescope/types-models";
import { useCallback, useEffect, useMemo } from "react";
import {
  buildTreeFromFields,
  countFormPages,
  countRemainingFormPages,
  isValueEmpty,
  resolveInitialFieldStack,
  resolveNodesToPush,
} from "../../utils";
import type { FormFieldNode, NextFieldLogicOptions } from "../../utils/types";
import type { LocalTellescopeFormResponseMap } from "../use-tellescope-form-core";
import type { FormShallowStoreHook } from "../../store";
import { activeNodeOf, pageIndexOf, popToPreviousPage, type FieldStack } from "./field-stack";
import { useTrackFieldView } from "./use-track-field-view";
import { useTrackFieldProgress } from "../use-form-analytics";

const percentageForPage = (pageIndex: number, totalPages: number) => ((pageIndex + 1) / totalPages) * 100;

export interface UseFormNavigationArgs {
  fieldsArr: TellescopeModelFromName<"form_fields">[] | undefined;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">>;
  subFieldIds: Set<string>;
  currentResponses: LocalTellescopeFormResponseMap;
  responsesMap: Record<string, FormResponseValue>;
  logicOptions: NextFieldLogicOptions;
  existingResponses: FormResponseValue[] | undefined;
  startingFieldId: string | undefined;
  onBeforeAdvance: (activeField: FormFieldNode) => void;
  responseId: string | undefined;
  responseAccessCode: string | undefined;
  useFormShallowStore: FormShallowStoreHook;
}

export const useFormNavigation = ({
  fieldsArr,
  fieldsMap,
  subFieldIds,
  currentResponses,
  responsesMap,
  logicOptions,
  existingResponses,
  startingFieldId,
  onBeforeAdvance,
  responseId,
  responseAccessCode,
  useFormShallowStore,
}: UseFormNavigationArgs) => {
  const fieldTree = useMemo(() => {
    if (!fieldsArr?.length) return undefined;
    return buildTreeFromFields(fieldsArr);
  }, [fieldsArr]);

  const hasNoRootField = !!fieldsArr?.length && !fieldTree;

  const existingResponseFieldIds = useMemo(
    () => new Set(existingResponses?.map((r) => r.fieldId) ?? []),
    [existingResponses]
  );

  const walkContext = useMemo(
    () => ({
      responsesByFieldId: currentResponses,
      allResponses: Object.values(currentResponses),
      logicOptions,
    }),
    [currentResponses, logicOptions]
  );

  // Resolved live until the enduser navigates, so a partly-filled form keeps reopening on the first
  // unanswered question as its answers load rather than freezing on whatever had loaded first.
  const openingStack = useMemo((): FieldStack | undefined => {
    if (!fieldTree) return undefined;

    return resolveInitialFieldStack({
      fieldTree,
      fieldsMap,
      answeredFieldIds: new Set(Object.keys(responsesMap)),
      existingResponseFieldIds,
      startingFieldId,
      walkContext: {
        ...walkContext,
        allResponses: walkContext.allResponses.filter((r) => !isValueEmpty(r.answer?.value)),
      },
    });
  }, [fieldTree, fieldsMap, responsesMap, startingFieldId, existingResponseFieldIds, walkContext]);

  const { fieldStack: storedFieldStack, setFieldStack } = useFormShallowStore("fieldStack", "setFieldStack");

  const fieldStack = useMemo(
    (): FieldStack => storedFieldStack ?? openingStack ?? [],
    [storedFieldStack, openingStack]
  );

  const activeField = activeNodeOf(fieldStack);

  const trackFieldView = useTrackFieldView({ responseId, accessCode: responseAccessCode });
  const trackFieldProgress = useTrackFieldProgress();

  // Navigation views are interaction-caused and reported by the handlers below; the opening field is
  // display-caused (nobody navigated to it), which is the one shape react.dev/learn/you-might-not-need-an-effect
  // keeps in an effect. Repeat runs are absorbed by the trackers' own per-field dedupe.
  const openingField = storedFieldStack === undefined && openingStack ? activeNodeOf(openingStack) : undefined;
  useEffect(() => {
    if (!openingField) return;
    trackFieldView(openingField.value.id);
    trackFieldProgress(openingField);
  }, [openingField, trackFieldView, trackFieldProgress]);

  const pageIndex = pageIndexOf(fieldStack, subFieldIds, fieldsMap);

  const totalPages = useMemo(() => {
    if (!fieldTree) return 1;
    return countFormPages(fieldTree, subFieldIds, walkContext, fieldsMap);
  }, [fieldTree, walkContext, subFieldIds, fieldsMap]);

  const progress = percentageForPage(pageIndex, totalPages);

  const nextPush = useMemo(() => {
    if (!activeField) return { nodes: [], reachedPage: false };
    return resolveNodesToPush({ activeField, fieldsMap, subFieldIds, walkContext });
  }, [activeField, walkContext, subFieldIds, fieldsMap]);

  // A trailing pass-through run with no page after it is never pushed: the active node must be a
  // page, and legacy left those answers out of the submission too.
  const nextField = nextPush.reachedPage ? nextPush.nodes[nextPush.nodes.length - 1] : undefined;

  const goToNextFieldInternal = useCallback(() => {
    if (!activeField || !nextField) return;

    onBeforeAdvance(activeField);
    setFieldStack([...fieldStack, ...nextPush.nodes]);
    trackFieldView(nextField.value.id);
    trackFieldProgress(nextField, activeField);
  }, [activeField, nextField, nextPush.nodes, onBeforeAdvance, fieldStack, setFieldStack, trackFieldView, trackFieldProgress]);

  const goToPreviousField = useCallback(() => {
    if (!activeField || activeField.value.options?.disableGoBack) return;

    const popped = popToPreviousPage(fieldStack, subFieldIds, fieldsMap);
    const previous = activeNodeOf(popped);
    if (popped === fieldStack || !previous) return;

    setFieldStack(popped);
    trackFieldView(previous.value.id);
    // Reported with the field being left as `previousTitle`, matching legacy, which read whatever
    // sat on top of its prev-field stack at the moment the event fired.
    trackFieldProgress(previous, activeField);
  }, [activeField, fieldStack, subFieldIds, fieldsMap, setFieldStack, trackFieldView, trackFieldProgress]);

  const goToField = useCallback(
    (fieldId: string) => {
      const targetIndex = fieldStack.findIndex((node) => node.value.id === fieldId);
      const target = fieldStack[targetIndex];
      if (!target || targetIndex === fieldStack.length - 1) return;

      setFieldStack(fieldStack.slice(0, targetIndex + 1));
      trackFieldView(target.value.id);
      trackFieldProgress(target, activeField);
    },
    [fieldStack, activeField, setFieldStack, trackFieldView, trackFieldProgress]
  );

  const isPreviousDisabled = pageIndex === 0 || !!activeField?.value.options?.disableGoBack;

  const showSubmit = !!activeField && !nextField;

  const getNumberOfRemainingPages = useCallback(() => {
    if (!activeField) return 0;
    return countRemainingFormPages(activeField.value, fieldsArr ?? [], subFieldIds);
  }, [activeField, fieldsArr, subFieldIds]);

  return {
    fieldStack,
    activeField,
    hasNoRootField,
    pageIndex,
    totalPages,
    progress,
    goToNextFieldInternal,
    goToPreviousField,
    goToField,
    isPreviousDisabled,
    showSubmit,
    getNumberOfRemainingPages,
  };
};
